// Hi-fi shaping for the batched voxel geometry cache: species proportions,
// build-time wear, the vertical ambient shade and faction trim. Split out of
// voxelGeometryCache.ts (stage v3).
import type { BlockVoxel, HiFiTraits } from '@aod/ui/prefabs';
import { VOXEL_SCALE } from '@aod/ui/prefabs';
import type * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { finishPartGeometry } from './voxelGeometryParts.js';

/**
 * Vertical ambient shading over the merged unit: feet ~18% darker than the
 * crown — a cheap AO read that stops smooth parts from looking flat-lit.
 */
export function applyVerticalShade(geo: THREE.BufferGeometry): void {
  geo.computeBoundingBox();
  const minY = geo.boundingBox?.min.y ?? 0;
  const spanY = Math.max(1e-6, (geo.boundingBox?.max.y ?? 1) - minY);
  const col = geo.getAttribute('color') as THREE.BufferAttribute;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - minY) / spanY;
    const shade = 0.82 + 0.18 * t;
    col.setXYZ(i, col.getX(i) * shade, col.getY(i) * shade, col.getZ(i) * shade);
  }
  col.needsUpdate = true;
}

// --- Stage v3: proportions, wear, faction trim ---------------------------------------------------------------

/** Parts that follow the species build (width/depth); weapons, shields and capes keep their authored size. */
export const BODY_PART_RE = /(torso|body|chest|robe|head|arm|leg|wing)/i;
const LIMB_PART_RE = /(arm|leg|wing)/i;

/**
 * Apply a species' proportions to one hi-fi part, in place: body parts scale in x/z around the unit's
 * vertical axis (`cx`, `cz`), limbs also thicken around their own axis. Heights are untouched, so a
 * champion is broader, not taller, and an archer lither, not shorter.
 */
export function applyProportions(
  geo: THREE.BufferGeometry,
  partName: string,
  traits: HiFiTraits | undefined,
  cx: number,
  cz: number,
): void {
  if (!traits || !BODY_PART_RE.test(partName)) return;
  const build = traits.build ?? 1;
  const limb = LIMB_PART_RE.test(partName) ? (traits.limb ?? 1) : 1;
  if (build === 1 && limb === 1) return;
  geo.computeBoundingBox();
  const b = geo.boundingBox;
  if (!b) return;
  const px = (b.min.x + b.max.x) / 2;
  const pz = (b.min.z + b.max.z) / 2;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    // Limb thickness about the part's own axis, then the body build about the unit's axis.
    const x = px + (pos.getX(i) - px) * limb;
    const z = pz + (pos.getZ(i) - pz) * limb;
    pos.setX(i, cx + (x - cx) * build);
    pos.setZ(i, cz + (z - cz) * build);
  }
  pos.needsUpdate = true;
}

/** Deterministic [0,1) hash of a position and a seed (stable across builds and machines). */
function wearHash(x: number, y: number, z: number, seed: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7 + z * 74.7 + seed * 19.19) * 43758.5453;
  return s - Math.floor(s);
}

/** Cheap string hash for a per-prefab wear seed. */
export function seedOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 9973;
  return h;
}

/**
 * Build-time wear and detail normals: a small seeded tilt of each vertex normal (so smooth parts catch the
 * light unevenly, like hand-worn leather and metal) and a few percent of colour variation that darkens
 * toward the underside. Pure per-vertex attribute edits: no new attributes, no new vertices, so the merge
 * contract holds. Team-coloured and emissive vertices are left alone (a glowing rune must not mottle).
 */
export function applyWear(geo: THREE.BufferGeometry, seed: number): void {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const nor = geo.getAttribute('normal') as THREE.BufferAttribute;
  const col = geo.getAttribute('color') as THREE.BufferAttribute;
  const team = geo.getAttribute('aTeam') as THREE.BufferAttribute | undefined;
  const emi = geo.getAttribute('aEmissive') as THREE.BufferAttribute | undefined;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const h1 = wearHash(x, y, z, seed);
    const h2 = wearHash(z, x, y, seed + 7);
    const h3 = wearHash(y, z, x, seed + 13);
    // Normal: tilt by up to ~0.1 rad, then renormalise.
    let nx = nor.getX(i) + (h1 - 0.5) * 0.2;
    let ny = nor.getY(i) + (h2 - 0.5) * 0.2;
    let nz = nor.getZ(i) + (h3 - 0.5) * 0.2;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    nor.setXYZ(i, nx, ny, nz);
    const special =
      (team ? team.getX(i) > 0 : false) ||
      (emi ? emi.getX(i) + emi.getY(i) + emi.getZ(i) > 0 : false);
    if (special) continue;
    // Colour wear: +-4% mottling, a touch darker where the normal faces down (grime in the creases).
    const mottle = 1 + (h1 - 0.5) * 0.08 - Math.max(0, -ny) * 0.04;
    col.setXYZ(i, col.getX(i) * mottle, col.getY(i) * mottle, col.getZ(i) * mottle);
  }
  nor.needsUpdate = true;
  col.needsUpdate = true;
}

/** Faction trim: gold-toned metal that picks up a share of the owner's colour (aTeam 0.35 in the shader). */
const TRIM_VOXEL = (x: number, y: number, z: number): BlockVoxel => ({
  x,
  y,
  z,
  color: '#d9c27a',
  metalness: 0.85,
  roughness: 0.3,
  team: false,
});
const TRIM_TEAM_SHARE = 0.35;
const TRIM_THICK = VOXEL_SCALE * 0.2;
/** How far the frame stands off the face: tiny, so a unit's bounding size barely moves. */
const TRIM_LIFT = VOXEL_SCALE * 0.02;

/**
 * Trim for a part's team voxels (tabards, shield faces, capes): a thin raised frame around the team region on
 * its front and back faces. One frame per part, eight small bars, so the cost stays fixed however big the
 * tabard is. Returns null when the part has no team voxels. The frame carries a fractional `aTeam`, so the
 * shared geometry shows each owner's colour in its own trim: one geometry per prefab still holds.
 */
export function teamTrim(voxels: readonly BlockVoxel[]): THREE.BufferGeometry | null {
  const team = voxels.filter((v) => v.team);
  if (team.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const v of team) {
    minX = Math.min(minX, v.x);
    minY = Math.min(minY, v.y);
    minZ = Math.min(minZ, v.z);
    maxX = Math.max(maxX, v.x + 1);
    maxY = Math.max(maxY, v.y + 1);
    maxZ = Math.max(maxZ, v.z + 1);
  }
  const w = (maxX - minX) * VOXEL_SCALE;
  const h = (maxY - minY) * VOXEL_SCALE;
  const cx = ((minX + maxX) / 2) * VOXEL_SCALE;
  const cy = ((minY + maxY) / 2) * VOXEL_SCALE;
  if (w < VOXEL_SCALE * 1.5 || h < VOXEL_SCALE * 1.5) return null; // too small to frame
  const bars: THREE.BufferGeometry[] = [];
  const bar = (sx: number, sy: number, sz: number, x: number, y: number, z: number) => {
    const g = new RoundedBoxGeometry(sx, sy, sz, 1, Math.min(sx, sy, sz) * 0.3);
    g.translate(x, y, z);
    bars.push(finishPartGeometry(g, TRIM_VOXEL(0, 0, 0), false));
  };
  for (const zFace of [minZ * VOXEL_SCALE - TRIM_LIFT, maxZ * VOXEL_SCALE + TRIM_LIFT]) {
    bar(w, TRIM_THICK, TRIM_THICK, cx, minY * VOXEL_SCALE + TRIM_THICK / 2, zFace);
    bar(w, TRIM_THICK, TRIM_THICK, cx, maxY * VOXEL_SCALE - TRIM_THICK / 2, zFace);
    bar(TRIM_THICK, h, TRIM_THICK, minX * VOXEL_SCALE + TRIM_THICK / 2, cy, zFace);
    bar(TRIM_THICK, h, TRIM_THICK, maxX * VOXEL_SCALE - TRIM_THICK / 2, cy, zFace);
  }
  for (const g of bars) {
    (g.getAttribute('aTeam') as THREE.BufferAttribute).array.fill(TRIM_TEAM_SHARE);
  }
  return mergeGeometries(bars, false);
}
